import React, { useEffect, useMemo, useState } from "react";
import { Layers, Search } from "lucide-react";
import { MainLayout } from "@/layouts/MainLayout";
import { useMockQuery } from "@/hooks/useMockQuery";
import { apiClient } from "@/lib/apiClient";
import { LoadingState } from "@/components/states/LoadingState";
import { ErrorState } from "@/components/states/ErrorState";
import { WhooLiveMap } from "@/components/map/WhooLiveMap";
import { MapFilters } from "@/components/map/MapFilters";
import { STATE_ORDER } from "@/lib/eventState";

export default function MapView() {
  const { data: events, loading, error, refetch } = useMockQuery(
    () => apiClient.getMapEvents(),
    []
  );
  const [activeStates, setActiveStates] = useState(STATE_ORDER);
  const [activeCategories, setActiveCategories] = useState([]);
  const [query, setQuery] = useState("");

  // Live refresh: pick up events created/edited from iOS/Android/Web.
  useEffect(() => {
    const t = setInterval(() => refetch(), 30000);
    return () => clearInterval(t);
  }, [refetch]);

  const categories = useMemo(() => {
    const m = new Map();
    (events || []).forEach((e) => {
      if (!m.has(e.category)) m.set(e.category, e.categoryColor);
    });
    return [...m.entries()].map(([key, color]) => ({ key, label: key, color }));
  }, [events]);

  const filtered = useMemo(() => {
    if (!events) return [];
    const q = query.trim().toLowerCase();
    return events.filter(
      (e) =>
        activeStates.includes(e.state) &&
        (activeCategories.length === 0 || activeCategories.includes(e.category)) &&
        (!q ||
          e.title.toLowerCase().includes(q) ||
          (e.location.city || "").toLowerCase().includes(q))
    );
  }, [events, activeStates, activeCategories, query]);

  const toggleState = (s) =>
    setActiveStates((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  const toggleCategory = (c) =>
    setActiveCategories((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  // Only block on the FIRST load; keep the map mounted during 30s background refreshes
  // so zoom/center and an open marker card are preserved.
  const initialLoading = loading && !events;
  const showError = error && !events;

  return (
    <MainLayout>
      <div className="relative h-[calc(100vh-4rem)] w-full overflow-hidden bg-[#eef3fb]">
        {initialLoading ? (
          <div className="absolute inset-0 flex items-center justify-center px-5">
            <div className="w-full max-w-3xl rounded-3xl bg-white/80 p-6 backdrop-blur-xl">
              <LoadingState count={3} />
            </div>
          </div>
        ) : showError ? (
          <div className="absolute inset-x-0 top-1/2 z-20 mx-auto max-w-lg -translate-y-1/2 px-5">
            <ErrorState error={error} onRetry={refetch} />
          </div>
        ) : (
          <WhooLiveMap events={filtered} height="100%" enableGeolocate />
        )}

        {/* Floating control panel */}
        {events && (
          <div className="pointer-events-auto absolute left-4 top-4 z-10 w-[calc(100%-2rem)] max-w-sm rounded-2xl border border-border bg-white/85 p-4 shadow-lg backdrop-blur-xl sm:left-6 sm:top-6">
            <div className="flex items-center justify-between">
              <h1 className="font-heading text-lg font-extrabold tracking-tight text-foreground">
                Live map
              </h1>
              <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <Layers className="h-4 w-4 text-brand" />
                {filtered.length}/{events?.length ?? 0}
              </span>
            </div>

            <div className="relative mt-3">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                data-testid="map-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search events or place…"
                className="w-full rounded-full border border-border bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-brand"
              />
            </div>

            <MapFilters
              className="mt-3 max-h-32 overflow-y-auto"
              activeStates={activeStates}
              onToggleState={toggleState}
              categories={categories}
              activeCategories={activeCategories}
              onToggleCategory={toggleCategory}
            />
          </div>
        )}
      </div>
    </MainLayout>
  );
}
